// ONE registry for the site's hubs and tools.
//
// It feeds the header, the footer (FooterMenu), the homepage "Explore" links,
// the compact nav + footer block inside every pre-rendered page, and the
// <title>/meta description of every hub (React <SEO> and the SSG script read
// the same entry, so they cannot drift).
//
// Dependency-free CommonJS on purpose, like src/utils/stateCost.js: the React
// app (webpack) and the build scripts (plain node) both load it.
//
// Rules for entries:
//   - href never changes (it is the URL).
//   - title is the base <title>; brandTitle() in src/utils/seoMeta.js adds
//     " | EVs for Idiots" only when the result still fits in 60 characters.
//   - description is the meta description: 120 to 160 characters, no em dashes.

const SITE = 'https://www.evsforidiots.com';
const SITE_NAME = 'EVs for Idiots';
const CONTACT_EMAIL = 'contact@adamapps.co';

const PAGES = [
  {
    key: 'incentives',
    href: '/incentives',
    label: 'EV incentives by state',
    navLabel: 'Incentives',
    blurb: 'What your state and utility still pay now that the federal credit is gone, checked state by state.',
    title: 'EV Incentives by State 2026: State & Utility Rebates',
    description:
      'The federal EV tax credit ended September 30, 2025. See what each state and utility still pays, which programs closed, and the EV fees nobody mentions.',
  },
  {
    key: 'ev-registration-fees',
    href: '/ev-registration-fees',
    label: 'EV registration fees by state',
    navLabel: 'EV Fees',
    blurb: 'The extra yearly fee each state charges EV owners, ranked, with plug-in hybrid fees and what is changing.',
    // Title and description are computed from the fee data (src/utils/evFees.js).
    dynamicMeta: true,
    title: 'EV Registration Fees by State: Every State Ranked',
    description:
      'Most states charge EV owners an extra registration fee. Every state ranked from highest to lowest, with plug-in hybrid fees and the states that charge none.',
  },
  {
    key: 'ev-vs-gas',
    href: '/ev-vs-gas',
    label: 'EV vs gas costs by state',
    navLabel: 'EV vs Gas',
    blurb: 'What 1,000 miles costs to charge at home versus fill up, with real EIA and AAA numbers for every state.',
    title: 'Cost to Charge an EV vs Gas, by State (2026)',
    description:
      "Charging at home beats gas in every state. See your state's real EIA electricity and AAA gas prices, what 1,000 miles costs each way, and what you'd save.",
  },
  {
    key: 'ev-cheaper-than-gas',
    href: '/is-an-ev-cheaper-than-gas',
    label: 'Is an EV cheaper than gas right now?',
    navLabel: 'EV or Gas?',
    blurb: 'The gas price at which charging at home costs the same per mile, nationally and in every state.',
    // Title and description are computed from the state cost data (src/utils/evCheaperThanGas.js).
    dynamicMeta: true,
    title: 'Is an EV Cheaper Than Gas Right Now?',
    description:
      'At the average U.S. home electricity rate, charging an EV costs the same per mile as cheap gas. The break-even gas price nationally and in every state.',
  },
  {
    key: 'find-your-ev',
    href: '/find-your-ev',
    label: 'Find your EV',
    navLabel: 'Find Your EV',
    blurb: 'The EVs you can buy in the US, with price, range and charging speed, filtered to how you actually drive.',
    title: 'Find Your EV: 2026 Electric Vehicle Buying Guide',
    description:
      'Compare the EVs sold in the US by price, EPA range, charging speed, body type and use case, then filter down to the right one for your life.',
  },
  {
    key: 'used-evs',
    href: '/used-evs',
    label: 'Used EV prices',
    navLabel: 'Used EVs',
    blurb: 'What 10 popular used EVs are listed for by model year, plus the battery, recall and warranty checks that matter.',
    // Title and description are computed from the used-EV data (src/utils/usedEvPages.js).
    dynamicMeta: true,
    title: 'Used EV Prices: What 10 Popular Models Cost Now',
    description:
      'Asking prices by model year for 10 popular used EVs, plus the battery health, recall and warranty checks that matter before you buy one.',
  },
  {
    key: 'compare',
    href: '/compare',
    label: 'Compare EVs head to head',
    navLabel: 'Compare',
    blurb: 'Popular EVs side by side: range, fast-charging speed, 0-60, seats and towing, and who should pick which.',
    title: 'Compare EVs Head to Head: Range, Charging and Specs',
    description:
      'Side-by-side comparisons of the most popular electric vehicles: EPA range, DC fast-charging speed, 0-60, seats and towing, and who should pick which.',
  },
  {
    key: 'savings-calculator',
    href: '/savings-calculator',
    label: 'EV savings calculator',
    navLabel: 'Savings',
    blurb: 'Your miles, your gas price, your electricity rate: what an EV would actually save you.',
    title: 'EV Savings Calculator: Fuel and Upkeep vs Gas',
    description:
      "How much would an EV actually save you? Enter your miles, your car's MPG and your ZIP to get the real fuel and maintenance math against your gas car.",
  },
  {
    key: 'chargers',
    href: '/chargers',
    label: 'Home EV chargers',
    navLabel: 'Chargers',
    blurb: 'Level 2 home chargers compared: amps, cable length, smart features and which ones are worth the money.',
    title: 'Best Home EV Chargers: Level 2 Picks Compared',
    description:
      'Wall-mounted and portable Level 2 home EV chargers compared: amps, cable length, smart features, pros and cons, and how to choose the right one.',
  },
  {
    key: 'deal-checker',
    href: '/deal-checker',
    label: 'Deal-Checker',
    navLabel: 'Deal-Checker',
    blurb: 'Paste your out-the-door sheet and see every junk fee to push back on. Free, nothing stored.',
    title: 'EV Deal-Checker: Spot Dealer Junk Fees',
    description:
      'Paste your out-the-door sheet. We flag every junk fee, bogus add-on and dead tax-credit claim an EV buyer should push back on, free, no signup, nothing stored.',
  },
  {
    key: 'dealership-survival-kit',
    href: '/dealership-survival-kit',
    label: 'Dealership Survival Kit',
    navLabel: 'Survival Kit',
    blurb: 'The printable cheat sheet: every junk fee to refuse and what to actually pay.',
    title: 'Dealership Survival Kit: Don’t Get Fleeced Buying an EV',
    description:
      'Every junk fee to decline, what to actually pay, and how to know more about the EV than the salesperson. A free, printable guide for EV buyers.',
  },
  {
    key: 'concierge',
    href: '/concierge',
    label: 'Get help buying an EV',
    navLabel: 'Get Help Buying',
    blurb: 'Tell us how you drive. We match you to the right EV and read the dealer quote before you sign. Free.',
    title: 'Free EV Buying Help: EV Match and Deal Review',
    description:
      'Tell us how you drive. We match you to the right EV, hand you the playbook, and read the dealer quote before you sign it. Free, and we never sell your email.',
  },
  {
    key: 'idiotic-statements',
    href: '/idiotic-statements',
    label: 'Idiotic statements, fact-checked',
    navLabel: 'Idiotic Statements',
    blurb: 'The things people say about EVs, each with a sourced True, False or Misleading verdict.',
    title: 'Idiotic Statements About EVs, Fact-Checked',
    description:
      'The most common things people say about electric vehicles, from fires to batteries to the grid, each with a sourced True, False or Misleading verdict.',
  },
  {
    key: 'blog',
    href: '/blog',
    label: 'The blog',
    navLabel: 'Blog',
    blurb: 'Plain-English guides to buying, charging and owning an EV, plus the news that matters.',
    title: 'EV Buying, Charging & Cost Guides',
    description:
      'Plain-English guides to buying, charging and owning an electric vehicle, plus the EV news that actually matters, without the fluff or the dealer BS.',
  },
  {
    key: 'brief',
    href: '/brief',
    label: 'This week in EVs',
    navLabel: 'The Brief',
    blurb: 'A short weekly digest of the EV news that actually matters to buyers and owners.',
    title: 'This Week in EVs: The Weekly Brief',
    description:
      'The EV news that actually matters this week, filtered for buyers and owners: new models, price moves, charging and incentive changes, in plain English.',
  },
  {
    key: 'how-it-works',
    href: '/how-it-works',
    label: 'How it works',
    navLabel: 'How It Works',
    blurb: 'What we do, what it costs (nothing), and exactly how we make money.',
    title: 'How It Works and How We Make Money',
    description:
      'What EVs for Idiots does, what it costs you (nothing), and exactly how we make money, including the one place we may earn a commission. No BS.',
  },
  {
    key: 'ev-quiz',
    href: '/ev-quiz',
    label: 'EV quiz',
    navLabel: 'EV Quiz',
    blurb: 'Six questions about budget, driving and seats, and we rank the EVs that fit.',
    title: 'EV Quiz: Find the Right Electric Vehicle for You',
    description:
      'Answer six quick questions about your budget, driving, seats and charging, and we rank the electric vehicles on the US market that fit your life best.',
  },
  {
    key: 'what-kind-of-idiot',
    href: '/what-kind-of-idiot',
    label: 'What kind of idiot are you?',
    navLabel: 'Idiot Quiz',
    blurb: 'Five quick questions and a shareable badge: angry, curious, hungry or EV advocate.',
    title: 'What Kind of EV Idiot Are You? The Quiz',
    description:
      'Angry, Curious, Hungry or EV Advocate? Five quick questions, a shareable badge, and the idiotic statements that match your flavor of idiot.',
  },
  {
    key: 'persona',
    href: '/persona',
    label: 'Idiot badges',
    navLabel: 'Idiot Badges',
    blurb: 'Every EV skeptic is one of a few flavors of idiot. Find yours.',
    title: 'The EV Idiot Personas: Angry, Curious, Hungry, Advocate',
    description:
      'Every EV skeptic is one of a few flavors of idiot: angry, curious or hungry. Find your badge, then find the sourced facts that match how you think.',
  },
  {
    key: 'mission',
    href: '/mission',
    label: 'Our mission',
    navLabel: 'Mission',
    blurb: 'Why this site exists and who it works for.',
    title: 'The Mission: Why EVs for Idiots Exists',
    description:
      'A lot of what you have heard about EVs is wrong on purpose. We answer it with sources, and we help real buyers get into EVs without getting fleeced.',
  },
  {
    // For partners who link to us (clubs, utilities, newsletters), not shoppers:
    // footer only, never the header nav. Content lives in src/config/shareKit.js.
    key: 'share',
    href: '/share',
    label: 'Share kit: link to us',
    navLabel: 'Link to Us',
    blurb: 'Copy-and-paste blurbs, embeddable fact-check cards and a badge for clubs, utilities, libraries and newsletters.',
    title: 'Free EV Outreach Toolkit: Blurbs, Badges, Fact-Check Cards',
    description:
      'Free copy-and-paste blurbs, a social post, embeddable fact-check cards and a badge for EV clubs, utilities, credit unions, libraries and newsletters.',
  },
];

const BY_KEY = Object.fromEntries(PAGES.map((p) => [p.key, p]));
const BY_HREF = Object.fromEntries(PAGES.map((p) => [p.href, p]));

// The hubs every page should be able to reach in one hop (homepage "Explore",
// footer). Order = how the homepage lists them.
const HUB_KEYS = [
  'incentives',
  'ev-registration-fees',
  'ev-vs-gas',
  'ev-cheaper-than-gas',
  'find-your-ev',
  'used-evs',
  'compare',
  'savings-calculator',
  'chargers',
  'idiotic-statements',
  'blog',
  'deal-checker',
  'concierge',
  'brief',
  'how-it-works',
];

// Footer columns.
const FOOTER_GROUPS = [
  { name: 'Save money', keys: ['incentives', 'ev-registration-fees', 'ev-vs-gas', 'ev-cheaper-than-gas', 'savings-calculator', 'chargers'] },
  { name: 'Shop smart', keys: ['find-your-ev', 'used-evs', 'compare', 'ev-quiz', 'deal-checker', 'dealership-survival-kit', 'concierge'] },
  { name: 'Learn', keys: ['idiotic-statements', 'blog', 'brief', 'what-kind-of-idiot', 'persona'] },
  { name: 'Company', keys: ['how-it-works', 'mission', 'share'], extra: [{ label: 'Contact us', href: `mailto:${CONTACT_EMAIL}` }] },
];

// The short top nav inside pre-rendered pages.
const SSG_NAV_KEYS = ['incentives', 'ev-vs-gas', 'find-your-ev', 'compare', 'idiotic-statements', 'blog'];

// Header nav sets, by the `page` prop PageHeader receives. Desktop rows stay
// short so they fit on one line next to the CTA chips; the mobile menu carries
// more. 'search' and 'content' are in-page actions on the homepage, not links.
const HEADER_NAV = {
  home: {
    desktop: ['@content', 'find-your-ev', 'deal-checker', 'dealership-survival-kit', 'incentives', 'blog'],
    mobile: ['@search', '@content', 'persona', 'find-your-ev', 'incentives', 'ev-vs-gas', 'deal-checker', 'dealership-survival-kit', 'concierge', 'brief', 'blog', 'mission'],
  },
  persona: { desktop: ['@home', 'find-your-ev', 'blog'], mobile: ['@home', 'find-your-ev', 'blog'] },
  mission: { desktop: ['@home', 'find-your-ev', 'blog'], mobile: ['@home', 'find-your-ev', 'blog'] },
  blog: { desktop: ['@home', 'find-your-ev', 'blog'], mobile: ['@home', 'find-your-ev', 'incentives', 'blog'] },
  'find-ev': {
    desktop: ['@home', 'find-your-ev', 'deal-checker', 'dealership-survival-kit', 'savings-calculator', 'incentives'],
    mobile: ['@home', 'find-your-ev', 'deal-checker', 'concierge', 'ev-quiz', 'savings-calculator', 'incentives', 'ev-vs-gas'],
  },
};

// Header label overrides (the header uses a few shorter/older labels).
const HEADER_LABELS = {
  '@search': 'Search',
  '@content': 'Idiotic statements',
  '@contentMobile': 'Idiotic Statements',
  '@home': 'Home',
  mission: 'Mission',
  'savings-calculator': 'Savings',
};

function hub(key) {
  const p = BY_KEY[key];
  if (!p) throw new Error(`siteRegistry: unknown page key "${key}"`);
  return p;
}

module.exports = {
  SITE,
  SITE_NAME,
  CONTACT_EMAIL,
  PAGES,
  BY_KEY,
  BY_HREF,
  HUB_KEYS,
  FOOTER_GROUPS,
  SSG_NAV_KEYS,
  HEADER_NAV,
  HEADER_LABELS,
  hub,
};
